import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Link,
  FormHelperText
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import SearchIcon from '@mui/icons-material/Search';
import AuthLayout from '../components/AuthLayout';
import clientService, { Client } from '../services/client.service';
import slaService, { SLA } from '../services/sla.service';
import geocodingService from '../services/geocoding.service';

const ClientForm: React.FC = () => {
  const { clientId } = useParams<{ clientId: string }>();
  const navigate = useNavigate();
  const isEditMode = clientId !== 'new';
  
  const [formData, setFormData] = useState<Partial<Client>>({
    name: '',
    documentType: 'CPF',
    documentNumber: '',
    clientType: 'individual',
    email: '',
    phone: '',
    address: {
      street: '',
      number: '',
      complement: '',
      neighborhood: '',
      city: '',
      state: '',
      zipCode: ''
    },
    contactPerson: {
      name: '',
      position: '',
      phone: '',
      email: ''
    },
    notes: '',
    isActive: true,
    sla: ''
  });
  
  const [slas, setSlas] = useState<SLA[]>([]);
  const [slaLoading, setSlaLoading] = useState(false);
  
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [geocodingLoading, setGeocodingLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  
  useEffect(() => {
    fetchSLAs();
    if (isEditMode) {
      fetchClient();
    }
  }, [clientId]);
  
  const fetchSLAs = async () => {
    try {
      setSlaLoading(true);
      const slaList = await slaService.getSLAs(true); // Buscar apenas SLAs ativos
      setSlas(slaList);
    } catch (err: any) {
      console.error('Erro ao buscar SLAs:', err);
      setError('Falha ao carregar SLAs disponíveis. Por favor, tente novamente.');
    } finally {
      setSlaLoading(false);
    }
  };
  
  const fetchClient = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await clientService.getClientById(clientId as string);
      setFormData(response.client);
    } catch (err: any) {
      console.error('Error fetching client:', err);
      setError('Falha ao carregar dados do cliente. Por favor, tente novamente.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => {
    const { name, value } = e.target;
    
    if (!name) return;
    
    if (name.includes('.')) {
      const parts = name.split('.');
      
      if (parts.length === 2) {
        // Handle nested properties (address and contactPerson)
        const [parent, child] = parts;
        setFormData(prev => ({
          ...prev,
          [parent]: {
            ...(prev[parent as keyof Client] as any || {}),
            [child]: value
          }
        }));
      } else if (parts.length === 3) {
        // Handle deeply nested properties (address.coordinates.lat)
        const [parent, middle, child] = parts;
        setFormData(prev => ({
          ...prev,
          [parent]: {
            ...(prev[parent as keyof Client] as any || {}),
            [middle]: {
              ...((prev[parent as keyof Client] as any)?.[middle] || {}),
              [child]: value === '' ? undefined : Number(value)
            }
          }
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };
  
  // Buscar endereço pelo CEP usando Google Maps Geocoding API
  const handleSearchAddress = async () => {
    const zipCode = formData.address?.zipCode;
    
    if (!zipCode || zipCode.length < 8) {
      return;
    }
    
    try {
      setGeocodingLoading(true);
      console.log('ClientForm: Buscando endereço para CEP:', zipCode);
      
      const addressData = await geocodingService.getAddressByZipCode(zipCode);
      
      if (addressData) {
        console.log('ClientForm: Endereço encontrado:', addressData);
        
        // Preencher campos do endereço
        setFormData(prev => ({
          ...prev,
          address: {
            ...prev.address,
            street: addressData.street || prev.address?.street || '',
            number: addressData.number || prev.address?.number || '',
            neighborhood: addressData.neighborhood || prev.address?.neighborhood || '',
            city: addressData.city || prev.address?.city || '',
            state: addressData.state || prev.address?.state || '',
            zipCode: addressData.zipCode,
            coordinates: {
              lat: addressData.coordinates.lat,
              lng: addressData.coordinates.lng
            }
          }
        }));
        
        setError('');
      } else {
        setError('CEP não encontrado. Preencha o endereço manualmente.');
      }
    } catch (error: any) {
      console.error('ClientForm: Erro ao buscar CEP:', error);
      setError('Erro ao buscar CEP. Verifique se o CEP está correto e tente novamente.');
    } finally {
      setGeocodingLoading(false);
    }
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setSaveLoading(true);
      setError('');
      setSuccess(false);
      
      // Criar uma cópia dos dados para evitar modificações diretas no estado
      const clientData = { ...formData };
      
      // Garantir que o SLA seja tratado corretamente
      if (clientData.sla) {
        // Se o SLA é um objeto, extrair apenas o ID
        if (typeof clientData.sla === 'object' && '_id' in clientData.sla) {
          console.log('SLA é um objeto, extraindo ID:', clientData.sla);
          clientData.sla = (clientData.sla as any)._id;
        }
        
        console.log('SLA após processamento:', clientData.sla);
      }
      
      if (isEditMode && clientId) {
        console.log(`Atualizando cliente ${clientId} com dados:`, clientData);
        await clientService.updateClient(clientId, clientData);
      } else {
        console.log('Criando novo cliente com dados:', clientData);
        await clientService.createClient(clientData as Omit<Client, '_id' | 'createdAt' | 'updatedAt'>);
      }
      
      setSuccess(true);
      console.log('Cliente salvo com sucesso!');
      
      // Redirect to client list after a short delay
      setTimeout(() => {
        navigate('/clients');
      }, 1500);
    } catch (err: any) {
      console.error('Error saving client:', err);
      
      if (err.response?.data?.error === 'duplicate_document') {
        setError('Já existe um cliente com este número de documento.');
      } else {
        setError(`Falha ao salvar cliente: ${err.message || 'Por favor, tente novamente.'}`);
      }
    } finally {
      setSaveLoading(false);
    }
  };
  
  const handleCancel = () => {
    navigate('/clients');
  };
  
  if (loading) {
    return (
      <AuthLayout title="Carregando Cliente">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress size={60} thickness={4} sx={{ color: '#2e7d32' }} />
        </Box>
      </AuthLayout>
    );
  }
  
  return (
    <AuthLayout title={isEditMode ? 'Editar Cliente' : 'Novo Cliente'}>
      <Box sx={{ mb: 4 }}>
        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
          <Link 
            color="inherit" 
            href="#" 
            onClick={(e) => { e.preventDefault(); navigate('/clients'); }}
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <Typography color="text.primary">Clientes</Typography>
          </Link>
          <Typography color="text.primary">
            {isEditMode ? 'Editar Cliente' : 'Novo Cliente'}
          </Typography>
        </Breadcrumbs>
        
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between', 
          alignItems: { xs: 'flex-start', sm: 'center' }, 
          gap: { xs: 2, sm: 0 },
          mb: 3 
        }}>
          <Typography variant="h4" sx={{ 
            fontWeight: 500,
            fontSize: { xs: '1.5rem', sm: '2rem' }
          }}>
            {isEditMode ? 'Editar Cliente' : 'Novo Cliente'}
          </Typography>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/clients')}
            size="small"
            sx={{ alignSelf: { xs: 'flex-start', sm: 'auto' } }}
          >
            Voltar
          </Button>
        </Box>
        
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            Cliente {isEditMode ? 'atualizado' : 'criado'} com sucesso!
          </Alert>
        )}
        
        <Paper sx={{ p: { xs: 2, sm: 3 } }}>
          <Box component="form" onSubmit={handleSubmit}>
            <Typography variant="h6" sx={{ mb: 2, fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
              Informações Básicas
            </Typography>
            
            <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={6} md={6}>
                <TextField
                  fullWidth
                  required
                  label="Nome"
                  name="name"
                  value={formData.name || ''}
                  onChange={handleInputChange}
                  size="small"
                />
              </Grid>
              
              <Grid item xs={6} sm={3} md={3}>
                <FormControl fullWidth required size="small">
                  <InputLabel id="document-type-label">Tipo de Documento</InputLabel>
                  <Select
                    labelId="document-type-label"
                    name="documentType"
                    value={formData.documentType || 'CPF'}
                    // @ts-ignore - Incompatibilidade de tipos entre React.ChangeEvent e SelectChangeEvent
                    onChange={handleInputChange}
                    label="Tipo de Documento"
                  >
                    <MenuItem value="CPF">CPF</MenuItem>
                    <MenuItem value="CNPJ">CNPJ</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={6} sm={3} md={3}>
                <TextField
                  fullWidth
                  required
                  label="Número do Documento"
                  name="documentNumber"
                  value={formData.documentNumber || ''}
                  onChange={handleInputChange}
                  size="small"
                />
              </Grid>
              
              <Grid item xs={6} sm={3} md={3}>
                <FormControl fullWidth required size="small">
                  <InputLabel id="client-type-label">Tipo de Cliente</InputLabel>
                  <Select
                    labelId="client-type-label"
                    name="clientType"
                    value={formData.clientType || 'individual'}
                    // @ts-ignore - Incompatibilidade de tipos entre React.ChangeEvent e SelectChangeEvent
                    onChange={handleInputChange}
                    label="Tipo de Cliente"
                  >
                    <MenuItem value="individual">Pessoa Física</MenuItem>
                    <MenuItem value="company">Empresa</MenuItem>
                    <MenuItem value="government">Governo</MenuItem>
                    <MenuItem value="other">Outro</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6} md={6}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email || ''}
                  onChange={handleInputChange}
                  size="small"
                />
              </Grid>
              
              <Grid item xs={6} sm={3} md={3}>
                <TextField
                  fullWidth
                  label="Telefone"
                  name="phone"
                  value={formData.phone || ''}
                  onChange={handleInputChange}
                  size="small"
                />
              </Grid>
              
              <Grid item xs={12} md={3}>
                <FormControl fullWidth required>
                  <InputLabel id="status-label">Status</InputLabel>
                  <Select
                    labelId="status-label"
                    name="isActive"
                    value={formData.isActive === undefined ? true : formData.isActive}
                    // @ts-ignore - Incompatibilidade de tipos entre React.ChangeEvent e SelectChangeEvent
                    onChange={handleInputChange}
                    label="Status"
                  >
                    {/* @ts-ignore - Valor booleano não é atribuível ao tipo esperado */}
                    <MenuItem value={"true"}>Ativo</MenuItem>
                    {/* @ts-ignore - Valor booleano não é atribuível ao tipo esperado */}
                    <MenuItem value={"false"}>Inativo</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth required error={!formData.sla}>
                  <InputLabel id="sla-label">Contrato de SLA</InputLabel>
                  <Select
                    labelId="sla-label"
                    id="sla"
                    name="sla"
                    value={
                      typeof formData.sla === 'object' && formData.sla && '_id' in formData.sla
                        ? (formData.sla as any)._id
                        : formData.sla || ''
                    }
                    // @ts-ignore - Incompatibilidade de tipos entre React.ChangeEvent e SelectChangeEvent
                    onChange={handleInputChange}
                    label="Contrato de SLA"
                    disabled={slaLoading}
                  >
                    {slas.map((sla) => (
                      <MenuItem key={sla._id} value={sla._id}>
                        {sla.name} ({sla.responseTime}h / {sla.resolutionTime}h)
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    {!formData.sla ? 'O contrato de SLA é obrigatório' : 
                     'Tempo de resposta / Tempo de resolução em horas'}
                  </FormHelperText>
                </FormControl>
              </Grid>
            </Grid>
            
            <Divider sx={{ my: 3 }} />
            
            <Typography variant="h6" sx={{ mb: 2 }}>
              Endereço
            </Typography>
            
            <Grid container spacing={3} sx={{ mb: 4 }}>
              {/* CEP com botão de consulta */}
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  required
                  label="CEP"
                  name="address.zipCode"
                  value={formData.address?.zipCode || ''}
                  onChange={handleInputChange}
                  placeholder="00000-000"
                  helperText="Digite o CEP"
                  disabled={geocodingLoading}
                />
              </Grid>
              
              <Grid item xs={12} md={2} sx={{ display: 'flex', alignItems: 'flex-start', pt: 1 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSearchAddress}
                  disabled={geocodingLoading || !formData.address?.zipCode || formData.address.zipCode.length < 8}
                  startIcon={geocodingLoading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                  fullWidth
                  sx={{ height: '56px' }}
                >
                  {geocodingLoading ? 'Buscando...' : 'Consultar'}
                </Button>
              </Grid>
              
              <Grid item xs={12} md={7}>
                <Alert severity="info" sx={{ height: '100%', display: 'flex', alignItems: 'center' }}>
                  Digite o CEP e clique em "Consultar" para preencher o endereço automaticamente
                </Alert>
              </Grid>
              
              {/* Rua */}
              <Grid item xs={12} md={8}>
                <TextField
                  fullWidth
                  required
                  label="Rua"
                  name="address.street"
                  value={formData.address?.street || ''}
                  onChange={handleInputChange}
                />
              </Grid>
              
              {/* Número */}
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  required
                  label="Número"
                  name="address.number"
                  value={formData.address?.number || ''}
                  onChange={handleInputChange}
                />
              </Grid>
              
              {/* Complemento */}
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  label="Complemento"
                  name="address.complement"
                  value={formData.address?.complement || ''}
                  onChange={handleInputChange}
                />
              </Grid>
              
              {/* Bairro */}
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  required
                  label="Bairro"
                  name="address.neighborhood"
                  value={formData.address?.neighborhood || ''}
                  onChange={handleInputChange}
                />
              </Grid>
              
              {/* Cidade */}
              <Grid item xs={12} md={5}>
                <TextField
                  fullWidth
                  required
                  label="Cidade"
                  name="address.city"
                  value={formData.address?.city || ''}
                  onChange={handleInputChange}
                />
              </Grid>
              
              {/* Estado */}
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  required
                  label="Estado"
                  name="address.state"
                  value={formData.address?.state || ''}
                  onChange={handleInputChange}
                  placeholder="SP"
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Latitude"
                  name="address.coordinates.lat"
                  type="number"
                  value={formData.address?.coordinates?.lat || ''}
                  onChange={handleInputChange}
                  helperText="Coordenada geográfica (ex: -23.550520)"
                  inputProps={{
                    step: "any"
                  }}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Longitude"
                  name="address.coordinates.lng"
                  type="number"
                  value={formData.address?.coordinates?.lng || ''}
                  onChange={handleInputChange}
                  helperText="Coordenada geográfica (ex: -46.633308)"
                  inputProps={{
                    step: "any"
                  }}
                />
              </Grid>
            </Grid>
            
            <Divider sx={{ my: 3 }} />
            
            <Typography variant="h6" sx={{ mb: 2 }}>
              Pessoa de Contato
            </Typography>
            
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Nome"
                  name="contactPerson.name"
                  value={formData.contactPerson?.name || ''}
                  onChange={handleInputChange}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Cargo"
                  name="contactPerson.position"
                  value={formData.contactPerson?.position || ''}
                  onChange={handleInputChange}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Telefone"
                  name="contactPerson.phone"
                  value={formData.contactPerson?.phone || ''}
                  onChange={handleInputChange}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Email"
                  name="contactPerson.email"
                  type="email"
                  value={formData.contactPerson?.email || ''}
                  onChange={handleInputChange}
                />
              </Grid>
            </Grid>
            
            <Divider sx={{ my: 3 }} />
            
            <Typography variant="h6" sx={{ mb: 2 }}>
              Observações
            </Typography>
            
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Observações"
                  name="notes"
                  multiline
                  rows={4}
                  value={formData.notes || ''}
                  onChange={handleInputChange}
                />
              </Grid>
            </Grid>
            
            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: { xs: 'stretch', sm: 'flex-end' }, 
              gap: { xs: 1, sm: 2 }, 
              mt: 4 
            }}>
              <Button
                variant="outlined"
                onClick={handleCancel}
                disabled={saveLoading}
                fullWidth={window.innerWidth < 600}
                size={window.innerWidth < 600 ? "medium" : "large"}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                startIcon={saveLoading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                disabled={saveLoading}
                fullWidth={window.innerWidth < 600}
                size={window.innerWidth < 600 ? "medium" : "large"}
                sx={{ 
                  backgroundColor: '#2e7d32',
                  '&:hover': {
                    backgroundColor: '#1b5e20',
                  }
                }}
              >
                {saveLoading ? 'Salvando...' : 'Salvar'}
              </Button>
            </Box>
          </Box>
        </Paper>
      </Box>
    </AuthLayout>
  );
};

export default ClientForm;
